import React, { useCallback, useEffect, useRef, useState } from "react";
import NavBar from "../components/NavBar";
import GenderBadge from "../components/GenderBadge";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import {
  SearchIcon,
  RefreshIcon,
  FilterIcon,
  ResetIcon,
  EditIcon,
} from "../components/icons";
import { formatDate } from "../utils/dateUtils";
import EditUserProfileModal from "../components/EditUserProfileModal";
import ProfileDetailsModal from "../components/ProfileDetailsModal";

// "Find Matches" — search profiles across every bureau on the platform.
// Two ways in:
//   (1) Manual filters (basic + advanced) → builds a /public/listings query.
//   (2) "Use my preferences" → reads the logged-in user's linked profile
//       preferences and applies them as filters in one click.
// Both paths render the same result grid below.

const createInitialFilters = () => ({
  search: "",
  gender: "",
  minAge: "",
  maxAge: "",
  city: "",
  profession: "",
  cast: "",
  maritalStatus: "",
  education: "",
  minHeightFeet: "",
  minHeightInches: "",
  maxHeightFeet: "",
  maxHeightInches: "",
});

// Convert a ft+in pair (either may be empty) to total inches. Returns null
// if both are blank so the caller can omit the query param entirely.
const toTotalInches = (ft, inch) => {
  const fNum = ft === "" || ft === null || ft === undefined ? null : Number(ft);
  const iNum = inch === "" || inch === null || inch === undefined ? null : Number(inch);
  const fValid = fNum !== null && !Number.isNaN(fNum);
  const iValid = iNum !== null && !Number.isNaN(iNum);
  if (!fValid && !iValid) return null;
  return (fValid ? fNum : 0) * 12 + (iValid ? iNum : 0);
};

const buildQuery = (filters) => {
  const sp = new URLSearchParams({ status: "active" });
  const {
    minHeightFeet,
    minHeightInches,
    maxHeightFeet,
    maxHeightInches,
    ...rest
  } = filters;
  Object.entries(rest).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    const s = typeof v === "string" ? v.trim() : `${v}`;
    if (s !== "") sp.append(k, s);
  });
  const minH = toTotalInches(minHeightFeet, minHeightInches);
  const maxH = toTotalInches(maxHeightFeet, maxHeightInches);
  if (minH !== null) sp.append("minHeight", String(minH));
  if (maxH !== null) sp.append("maxHeight", String(maxH));
  return sp.toString();
};

const FindMatches = () => {
  const [filters, setFilters] = useState(createInitialFilters);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [criteria, setCriteria] = useState(null); // last-applied criteria summary
  const [usingMyPrefs, setUsingMyPrefs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [detailsUserId, setDetailsUserId] = useState(null);
  const myProfileRef = useRef(null);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  // Pull the logged-in user's profile once so we can exclude it from the
  // results and so the "Use my preferences" button can run without an
  // extra round-trip.
  useEffect(() => {
    let cancelled = false;
    apiFetch("/public/auth/my-profile")
      .then((data) => {
        if (!cancelled) myProfileRef.current = data;
      })
      .catch(() => {
        /* no-op: user may not yet have a linked profile */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runSearch = useCallback(
    async (effectiveFilters, summary, multiNarrow) => {
      try {
        setLoading(true);
        setError("");
        setGlobalLoadingMessage("Searching matches…");
        setGlobalLoading(true);

        const res = await apiFetch(`/public/listings?${buildQuery(effectiveFilters)}`);
        let list = res?.data || [];

        // Exclude the caller's own profile.
        const myId = myProfileRef.current?._id;
        if (myId) list = list.filter((p) => p._id !== myId);

        // Optional multi-value narrowing for preferences with several entries
        // (city/profession/caste lists). Case-insensitive intersection.
        if (multiNarrow) {
          const ciIncludes = (arr, val) =>
            Array.isArray(arr) &&
            arr.length > 0 &&
            arr.some(
              (x) =>
                typeof x === "string" &&
                typeof val === "string" &&
                x.trim().toLowerCase() === val.trim().toLowerCase()
            );
          list = list.filter((p) => {
            if (
              multiNarrow.cities?.length > 1 &&
              !ciIncludes(multiNarrow.cities, p.city)
            )
              return false;
            if (
              multiNarrow.professions?.length > 1 &&
              !ciIncludes(multiNarrow.professions, p.profession)
            )
              return false;
            if (multiNarrow.castes?.length > 1) {
              const c = p.cast || p.caste;
              if (!ciIncludes(multiNarrow.castes, c)) return false;
            }
            return true;
          });
        }

        setProfiles(list);
        setCriteria(summary || null);
      } catch (err) {
        console.error("Find matches failed:", err);
        setError(err.message || "Failed to load matches");
      } finally {
        setLoading(false);
        setGlobalLoading(false);
        setGlobalLoadingMessage("");
      }
    },
    [setGlobalLoading, setGlobalLoadingMessage]
  );

  // Manual search: build query directly from the form.
  const handleApplyFilters = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setUsingMyPrefs(false);
    runSearch(filters, {
      mode: "manual",
      ...filters,
    });
  };

  const handleResetFilters = () => {
    const reset = createInitialFilters();
    setFilters(reset);
    setShowAdvanced(false);
    setUsingMyPrefs(false);
    runSearch(reset, { mode: "manual" });
  };

  // Preference-based search: read prefs from the linked profile, push them
  // into the form so the user can refine, then run.
  const handleUseMyPreferences = async () => {
    try {
      setGlobalLoadingMessage("Loading your preferences…");
      setGlobalLoading(true);
      const me = myProfileRef.current || (await apiFetch("/public/auth/my-profile"));
      myProfileRef.current = me;

      const splitInches = (total) =>
        typeof total === "number"
          ? { ft: String(Math.floor(total / 12)), inch: String(total % 12) }
          : { ft: "", inch: "" };
      const minH = splitInches(me?.preferredHeightMin);
      const maxH = splitInches(me?.preferredHeightMax);

      const pref = {
        gender:
          me?.preferredGender && me.preferredGender !== "any"
            ? me.preferredGender
            : "",
        minAge: me?.preferredAgeRange?.min ?? "",
        maxAge: me?.preferredAgeRange?.max ?? "",
        city: me?.preferredCities?.[0] || "",
        profession: me?.preferredProfessions?.[0] || "",
        cast: me?.preferredCastes?.[0] || "",
        maritalStatus: "",
        education: "",
        search: "",
        minHeightFeet: minH.ft,
        minHeightInches: minH.inch,
        maxHeightFeet: maxH.ft,
        maxHeightInches: maxH.inch,
      };
      setFilters(pref);
      setUsingMyPrefs(true);
      runSearch(
        pref,
        {
          mode: "preferences",
          preferredGender: me?.preferredGender || "any",
          preferredAgeRange: me?.preferredAgeRange || null,
          preferredCities: me?.preferredCities || [],
          preferredProfessions: me?.preferredProfessions || [],
          preferredCastes: me?.preferredCastes || [],
          preferredHeightMin:
            typeof me?.preferredHeightMin === "number"
              ? me.preferredHeightMin
              : null,
          preferredHeightMax:
            typeof me?.preferredHeightMax === "number"
              ? me.preferredHeightMax
              : null,
        },
        {
          cities: me?.preferredCities || [],
          professions: me?.preferredProfessions || [],
          castes: me?.preferredCastes || [],
        }
      );
    } catch (err) {
      console.error("Use my preferences failed:", err);
      setError(err.message || "Failed to load your preferences");
    } finally {
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  // Initial load — show all active profiles.
  useEffect(() => {
    runSearch(createInitialFilters(), { mode: "manual" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const arrLabel = (a) =>
    Array.isArray(a) && a.length > 0 ? a.join(", ") : "Any";

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      {/* Sticky page header */}
      <div className="sticky top-16 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
                <SearchIcon className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                  Find Matches
                </h1>
                <p className="text-xs text-[var(--color-text-muted)]">
                  Search active profiles across every bureau
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                usingMyPrefs ? handleUseMyPreferences() : handleApplyFilters()
              }
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
            >
              <RefreshIcon className="w-4 h-4" />
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Filter card */}
        <form
          onSubmit={handleApplyFilters}
          className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 space-y-3"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block mb-1">Search</label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={onFilterChange}
                placeholder="Name, reference ID…"
              />
            </div>
            <div>
              <label className="block mb-1">Gender</label>
              <select name="gender" value={filters.gender} onChange={onFilterChange}>
                <option value="">Any</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <label className="block mb-1">Min Age</label>
              <input
                type="number"
                name="minAge"
                value={filters.minAge}
                onChange={onFilterChange}
                min="18"
                max="100"
              />
            </div>
            <div>
              <label className="block mb-1">Max Age</label>
              <input
                type="number"
                name="maxAge"
                value={filters.maxAge}
                onChange={onFilterChange}
                min="18"
                max="100"
              />
            </div>
          </div>

          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
              <div>
                <label className="block mb-1">City</label>
                <input
                  type="text"
                  name="city"
                  value={filters.city}
                  onChange={onFilterChange}
                />
              </div>
              <div>
                <label className="block mb-1">Profession</label>
                <input
                  type="text"
                  name="profession"
                  value={filters.profession}
                  onChange={onFilterChange}
                />
              </div>
              <div>
                <label className="block mb-1">Caste</label>
                <input
                  type="text"
                  name="cast"
                  value={filters.cast}
                  onChange={onFilterChange}
                />
              </div>
              <div>
                <label className="block mb-1">Marital Status</label>
                <select
                  name="maritalStatus"
                  value={filters.maritalStatus}
                  onChange={onFilterChange}
                >
                  <option value="">Any</option>
                  <option value="single">Single</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </div>
              <div>
                <label className="block mb-1">Min Height</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    name="minHeightFeet"
                    value={filters.minHeightFeet}
                    onChange={onFilterChange}
                    min="0"
                    max="8"
                    placeholder="ft"
                  />
                  <input
                    type="number"
                    name="minHeightInches"
                    value={filters.minHeightInches}
                    onChange={onFilterChange}
                    min="0"
                    max="11"
                    placeholder="in"
                  />
                </div>
              </div>
              <div>
                <label className="block mb-1">Max Height</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    name="maxHeightFeet"
                    value={filters.maxHeightFeet}
                    onChange={onFilterChange}
                    min="0"
                    max="8"
                    placeholder="ft"
                  />
                  <input
                    type="number"
                    name="maxHeightInches"
                    value={filters.maxHeightInches}
                    onChange={onFilterChange}
                    min="0"
                    max="11"
                    placeholder="in"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] font-medium transition-colors"
            >
              {showAdvanced ? "Hide advanced filters" : "Show advanced filters"}
            </button>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setIsEditOpen(true)}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
              >
                <EditIcon className="w-4 h-4" />
                Edit preferences
              </button>
              <button
                type="button"
                onClick={handleUseMyPreferences}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-sm font-semibold transition-colors"
              >
                <SearchIcon className="w-4 h-4" />
                Use my preferences
              </button>
              <button
                type="button"
                onClick={handleResetFilters}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
              >
                <ResetIcon className="w-4 h-4" />
                Reset
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-sm font-semibold transition-colors"
              >
                <FilterIcon className="w-4 h-4" />
                Apply Filters
              </button>
            </div>
          </div>
        </form>

        {/* When "Use my preferences" was the last action, surface the
            criteria summary so the user knows what's being matched. */}
        {usingMyPrefs && criteria?.mode === "preferences" && (
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 text-xs">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
              Your preferences
            </div>
            <div className="grid gap-1 sm:grid-cols-2 md:grid-cols-3">
              <div>
                <span className="font-medium">Gender:</span>{" "}
                <span className="capitalize">
                  {criteria.preferredGender || "Any"}
                </span>
              </div>
              <div>
                <span className="font-medium">Age:</span>{" "}
                {criteria.preferredAgeRange &&
                (criteria.preferredAgeRange.min != null ||
                  criteria.preferredAgeRange.max != null)
                  ? `${criteria.preferredAgeRange.min ?? "—"}–${criteria.preferredAgeRange.max ?? "—"}`
                  : "Any"}
              </div>
              <div>
                <span className="font-medium">Cities:</span>{" "}
                {arrLabel(criteria.preferredCities)}
              </div>
              <div>
                <span className="font-medium">Professions:</span>{" "}
                {arrLabel(criteria.preferredProfessions)}
              </div>
              <div>
                <span className="font-medium">Castes:</span>{" "}
                {arrLabel(criteria.preferredCastes)}
              </div>
              <div>
                <span className="font-medium">Height:</span>{" "}
                {criteria.preferredHeightMin != null ||
                criteria.preferredHeightMax != null
                  ? `${
                      criteria.preferredHeightMin != null
                        ? `${Math.floor(criteria.preferredHeightMin / 12)}'${criteria.preferredHeightMin % 12}"`
                        : "—"
                    }–${
                      criteria.preferredHeightMax != null
                        ? `${Math.floor(criteria.preferredHeightMax / 12)}'${criteria.preferredHeightMax % 12}"`
                        : "—"
                    }`
                  : "Any"}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
            {error}
          </div>
        )}

        {!loading && profiles.length === 0 && !error && (
          <div className="text-center py-12 text-sm text-[var(--color-text-muted)]">
            No matching profiles right now. Try different filters or update your
            preferences from <span className="font-medium">Edit Profile</span>.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {profiles.map((p) => {
            const heightStr =
              p.heightFeet || p.heightInches
                ? `${p.heightFeet ?? "—"}ft ${p.heightInches ?? 0}in`
                : null;
            return (
              <div
                key={p._id}
                role="button"
                tabIndex={0}
                onClick={() => setDetailsUserId(p._id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetailsUserId(p._id);
                  }
                }}
                className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 hover:shadow-md hover:border-[var(--color-primary)] cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              >
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
                  ID {p.referenceId || "—"}
                </div>
                <div className="flex items-center gap-2 mb-3">
                  {p.fullName && (
                    <h3 className="text-base font-semibold text-[var(--color-text-strong)] truncate">
                      {p.fullName}
                    </h3>
                  )}
                  <GenderBadge gender={p.gender} />
                </div>

                <div className="space-y-1 text-xs">
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--color-text-muted)]">Age</span>
                    <span className="text-[var(--color-text)] font-medium">
                      {p.age ?? "—"}
                    </span>
                  </div>
                  {heightStr && (
                    <div className="flex justify-between gap-2">
                      <span className="text-[var(--color-text-muted)]">Height</span>
                      <span className="text-[var(--color-text)] font-medium">
                        {heightStr}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--color-text-muted)]">City</span>
                    <span className="text-[var(--color-text)] font-medium truncate">
                      {p.city || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--color-text-muted)]">Profession</span>
                    <span className="text-[var(--color-text)] font-medium truncate">
                      {p.profession || "—"}
                    </span>
                  </div>
                  {(p.cast || p.caste) && (
                    <div className="flex justify-between gap-2">
                      <span className="text-[var(--color-text-muted)]">Caste</span>
                      <span className="text-[var(--color-text)] font-medium truncate">
                        {p.cast || p.caste}
                      </span>
                    </div>
                  )}
                  {p.maritalStatus && (
                    <div className="flex justify-between gap-2">
                      <span className="text-[var(--color-text-muted)]">Marital</span>
                      <span className="text-[var(--color-text)] font-medium capitalize">
                        {p.maritalStatus}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex items-center justify-between gap-2 text-[10px] text-[var(--color-text-muted)]">
                  <span className="truncate">
                    {p.bureau?.name && <>Listed by {p.bureau.name}</>}
                    {p.bureau?.name && p.createdAt && <> · </>}
                    {p.createdAt && formatDate(p.createdAt)}
                  </span>
                  <span className="inline-flex items-center gap-1 shrink-0">
                    <svg
                      viewBox="0 0 24 24"
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    {p.views ?? 0}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit preferences modal — re-fetches the user's linked profile on
          save so subsequent "Use my preferences" runs see the latest values. */}
      <EditUserProfileModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSaved={() => {
          setIsEditOpen(false);
          // Invalidate cached profile so the next prefs read is fresh, then
          // immediately re-run the preferences-based search.
          myProfileRef.current = null;
          handleUseMyPreferences();
        }}
      />

      <ProfileDetailsModal
        isOpen={!!detailsUserId}
        userId={detailsUserId}
        onClose={() => setDetailsUserId(null)}
      />
    </div>
  );
};

export default FindMatches;
